import { useMemo } from 'react'
import * as React from 'react'
import useToggle from 'hooks/useToggle'
import { skip, take } from 'lib/array/arrayUtils'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Expander from 'components/utils/Expander'
import Group from 'components/utils/Group'
import Typography from 'LuxKit/Typography/Typography'
import RadioInput from 'LuxKit/Radio/RadioInput'
import type { FilterPanelCheckItem } from './FilterPanelCheckboxGroup'
import { FormattedMessage, useIntl } from 'react-intl'

interface Props {
  name: string
  items: Array<FilterPanelCheckItem>
  defaultSelected?: string | number
  selected?: string | number
  maxItems?: number
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  titlecase?: boolean
  allowShowMore?: boolean
}

function FilterPanelRadioGroup(props: Props) {
  const {
    onChange,
    name,
    items,
    maxItems = Infinity,
    titlecase = false,
    allowShowMore = true,
    selected,
    defaultSelected,
  } = props
  const intl = useIntl()
  const [showMore, toggleShowMore] = useToggle(!allowShowMore)
  const visibleItems = useMemo(() => items.filter(item => !item.hidden), [items])
  const hasMore = visibleItems.length > maxItems

  const showMoreLabel = showMore
    ? intl.formatMessage({
        id: 'E-dBLYz7ysQuOFfJPr__',
        defaultMessage: 'Show less',
        description: 'Toggle button label to collapse and show fewer radio filter options',
      })
    : intl.formatMessage({
        id: 'qjcZ8JhqeSIzTiLHANBz',
        defaultMessage: 'Show more',
        description: 'Toggle button label to expand and show more radio filter options',
      })

  return <>
    <Group gap={8} direction="vertical">
      {take(visibleItems, maxItems).map(item =>
        <RadioInput
          key={item.value}
          name={name}
          value={item.value}
          onChange={onChange}
          hidden={item.hidden}
          defaultChecked={defaultSelected === item.value}
          checked={selected !== undefined ? selected === item.value : undefined}
          data-carrier={item.value}
          size="medium"
        >
          {!!item.count && !item.hideCount && <FormattedMessage
            id="cwBKmNoKc8qEF-Xjwbcz"
            defaultMessage="{label} ({count})"
            description="Radio filter option label with its matching result count, e.g. Economy (12)"
            values={{ label: item.label, count: item.count }}
          />}
          {(!item.count || item.hideCount) &&
            <Typography format={titlecase ? 'titlecase' : undefined} as="span">{item.label}</Typography>}
        </RadioInput>,
      )}
    </Group>
    {hasMore && <>
      <Expander expanded={showMore}>
        <Group gap={8} direction="vertical">
          {skip(visibleItems, maxItems).map(item =>
            <RadioInput
              key={item.value}
              name={name}
              value={item.value}
              onChange={onChange}
              hidden={item.hidden}
              defaultChecked={defaultSelected === item.value}
              checked={selected !== undefined ? selected === item.value : undefined}
              data-carrier={item.value}
              size="medium"
            >
              {!!item.count && !item.hideCount && <FormattedMessage
                id="cwBKmNoKc8qEF-Xjwbcz"
                defaultMessage="{label} ({count})"
                description="Radio filter option label with its matching result count, e.g. Economy (12)"
                values={{ label: item.label, count: item.count }}
              />}
              {(!item.count || item.hideCount) &&
                <Typography format={titlecase ? 'titlecase' : undefined} as="span">{item.label}</Typography>}
            </RadioInput>,
          )}
        </Group>
      </Expander>
      {allowShowMore && <SmallToggleButton
        on={showMore}
        onClick={toggleShowMore}
        size="medium"
        iconPosition="start"
      >
        {showMoreLabel}
      </SmallToggleButton>}
    </>}
  </>
}

export default React.memo(FilterPanelRadioGroup)
